import { get, set } from "hassibot/local-storage";

/**
 * Per-user opt-in toward the manual "waiting + manual update" deployment mode.
 *
 * Default (key absent / not "true") = current behaviour: a new Service Worker
 * activates immediately (auto `skipWaiting`) with no prompt. When enabled, a new
 * version is held in `waiting` and applied through a manual, coordinated reload —
 * auto-upgraded on a morning return, or via the discreet footer "Mettre à jour"
 * button during the day (see app-version-update.store).
 *
 * This module is pure and synchronous (no React, no Service Worker import) so it
 * can be read from anywhere — the history wrapper, the store, the footer toggle —
 * without risking an import cycle with the React store.
 */
const DEPLOYMENT_OPT_IN_KEY = "HB_DEPLOYMENT_OPT_IN";

// Last value written this session, kept in memory so it can be re-applied after a
// `localStorage.clear()` (logout / app-update nuke) — same trick as DEVICE_ID.
let lastKnownValue: boolean | null = null;

export const isDeploymentOptInEnabled = (): boolean => {
  const enabled = get(DEPLOYMENT_OPT_IN_KEY) === "true";
  // Seed the in-memory cache on every read (like DEVICE_ID's lazy getter), so a
  // preference set in a *previous* session — and merely read this session, e.g.
  // by the history wrapper on the first navigation — still survives a later
  // localStorage.clear() (logout / nuke) via restoreDeploymentOptInAfterReset().
  lastKnownValue = enabled;
  return enabled;
};

export const setDeploymentOptInEnabled = (enabled: boolean): void => {
  lastKnownValue = enabled;
  set(DEPLOYMENT_OPT_IN_KEY, enabled ? "true" : "false");
};

/**
 * Re-write the preference after a `localStorage.clear()` so an opted-in user
 * keeps the manual update mode across logout / nuke. No-op until the preference
 * has been touched this session.
 */
export const restoreDeploymentOptInAfterReset = (): void => {
  if (lastKnownValue !== null) {
    set(DEPLOYMENT_OPT_IN_KEY, lastKnownValue ? "true" : "false");
  }
};

// Seed the in-memory cache at module load (like DEVICE_ID being read very early
// at boot), so the preference survives a localStorage.clear() (logout / nuke)
// even in a tab that never navigated nor evaluated the SW this session — where
// the read-time seeding above would otherwise never fire and
// restoreDeploymentOptInAfterReset() would be a no-op.
isDeploymentOptInEnabled();
